/**
 * THE MUSTER - everyone the viewer may act as, as one column of chips
 * (2026-10-22, plan `muster`; rulings in memory `muster-rulings`).
 *
 * The ORDER is the order rail's (`railRows`): the tracker's seats in
 * initiative order, then whoever is not tracked. This module decides the
 * column's SHAPE on top of it:
 *
 *   - one chip per combatant; custom seats and ghosts (no combatant, no
 *     projection) are not someone you can act as, so they are left out
 *   - a CONTROL NETWORK folds under its controller: the units sit indented
 *     directly beneath the controller's chip, wherever that sits, in their
 *     own order. The controller is `controllerFor`'s - the first flagged one;
 *     a second flagged controller folds in as a unit. A tag one combatant
 *     carries is not a network (`networkMembers` < 2), as the squad bar rules
 *   - with the controller absent, the units keep their own seats, marked
 *     `orphan` for the GM only: a player never receives a hidden figure, so
 *     to them a hidden controller looks absent and the mark would say one
 *     exists
 *   - a block (the host and its units) lands in its HOST's section
 *
 * A chip carries data only - the vitals as numbers, never the Target - so a
 * memo can compare it field by field (`sameChip`) whatever the projection
 * does to object identity. `fitKey` is the column's structure alone, so the
 * fit (how dense the chips are drawn) re-measures on a new chip or a fold,
 * never on a wound.
 *
 * Pure, zero React dependency.
 */
import { inSquadMode } from '../killTeam/marks';
import { isDowned } from '../conditions';
import { vitalsOf } from '../healthRamp';
import { controlOf } from './control';
import type { RailRow } from './orderRail';
import type { Livery } from './team';
import type { Combatant } from './types';
import type { Target } from '../../types';
import { isObject } from '../bodyShape';

export interface MusterChip {
  id: string;
  name: string;
  initiative: number | null;
  livery: Livery | null;
  /** Wounds, or magnitude for a horde (`vitalsOf`). */
  shown: number;
  total: number;
  isHorde: boolean;
  /** The one this screen acts as. */
  acting: boolean;
  /** Whose turn it is. */
  turn: boolean;
  /** Has acted this round. */
  acted: boolean;
  /** Owes a reaction now. */
  owed: boolean;
  /** A GM's hidden figure - only a GM screen holds one. */
  hidden: boolean;
  downed: boolean;
  /** 1 = a unit folded under its controller. */
  depth: 0 | 1;
  /** Hosts a network's units. */
  controls: boolean;
  /** In a network whose controller is absent - told to the GM only. */
  orphan: boolean;
  /** In the network of the one this screen acts as. */
  sibling: boolean;
  /** In Squad Mode (2026-10-23): the chip wears the linked chevrons. */
  squad: boolean;
  /** The network's label, or null outside one. */
  network: string | null;
}

/** A host chip and the units folded under it (none for most). */
export interface MusterBlock {
  key: string;
  chips: MusterChip[];
}

export interface Muster {
  inOrder: MusterBlock[];
  notInOrder: MusterBlock[];
}

export interface MusterInput {
  rows: readonly RailRow[];
  roster: readonly Combatant[];
  actorId: string | null;
  owedIds: readonly string[];
  viewerIsGm: boolean;
}

/** A row that is a person this screen could act as. */
type PersonRow = RailRow & { combatantId: string; target: Target };

// An Object is never acted as (2026-10-26), so it never stands in the muster.
const isPerson = (r: RailRow): r is PersonRow => !!r.combatantId && !!r.target && !isObject(r.target);

/** Each combatant's network: its tag, label and host, or nothing for one that is not a network. */
interface Membership {
  tag: string;
  label: string;
  hostId: string | null;
}

type Networks = Map<string, { count: number; hostId: string | null }>;

/** Count one combatant into its tag's network; the first flagged controller hosts it (`controllerFor`'s rule). */
function tally(nets: Networks, c: Combatant): void {
  const control = controlOf(c);
  if (!control?.tag) return;
  const net = nets.get(control.tag) ?? { count: 0, hostId: null };
  net.count += 1;
  if (control.isController && !net.hostId) net.hostId = c.id;
  nets.set(control.tag, net);
}

/** Every tag on the roster, in one pass: how many carry it, and who hosts it. */
function networksOf(roster: readonly Combatant[]): Networks {
  const nets: Networks = new Map();
  roster.forEach((c) => tally(nets, c));
  return nets;
}

/**
 * A combatant's membership, or null outside a network - a tag one combatant carries is none (`networkOfActor`'s
 * rule). A host must stand in the muster to fold anyone under it (`onStage`, the ids with a chip): a controller on
 * the roster with no chip leaves its units at their own seats, as an absent one does.
 */
function membershipOf(c: Combatant | undefined, nets: Networks, onStage: ReadonlySet<string>): Membership | null {
  const control = controlOf(c);
  const net = control?.tag ? nets.get(control.tag) : undefined;
  if (!control || !net || net.count < 2) return null;
  const hostId = net.hostId && onStage.has(net.hostId) ? net.hostId : null;
  return { tag: control.tag!, label: control.label || control.tag!, hostId };
}

/** Everything a chip needs from its row, its network and the viewer. */
interface ChipContext {
  input: MusterInput;
  owed: ReadonlySet<string>;
  memberships: ReadonlyMap<string, Membership | null>;
  actorTag: string | null;
}

function chipOf(row: PersonRow, depth: 0 | 1, cx: ChipContext): MusterChip {
  const m = cx.memberships.get(row.combatantId) ?? null;
  const { shown, total, isHorde } = vitalsOf(row.target);
  const acting = row.combatantId === cx.input.actorId;
  return {
    id: row.combatantId,
    name: row.name,
    initiative: row.initiative,
    livery: row.livery,
    shown,
    total,
    isHorde,
    acting,
    turn: row.status === 'active',
    acted: row.status === 'done',
    owed: cx.owed.has(row.combatantId),
    hidden: row.hidden,
    downed: isDowned(row.target.conditions),
    depth,
    controls: !!m && m.hostId === row.combatantId,
    orphan: !!m && !m.hostId && cx.input.viewerIsGm,
    sibling: !acting && !!m && m.tag === cx.actorTag,
    squad: inSquadMode(row.target),
    network: m?.label ?? null,
  };
}

/** A unit whose controller is on the stage: it is drawn under them, not at its own seat. */
function hostOf(row: PersonRow, cx: ChipContext): string | null {
  const hostId = cx.memberships.get(row.combatantId)?.hostId ?? null;
  return hostId !== row.combatantId ? hostId : null;
}

/** The host's block: their chip, then their units (in row order). */
function blockOf(row: PersonRow, units: readonly PersonRow[], cx: ChipContext): MusterBlock {
  return { key: row.combatantId, chips: [chipOf(row, 0, cx), ...units.map((u) => chipOf(u, 1, cx))] };
}

/** The muster's blocks, split by section. See the header. One pass over the roster for its networks. */
export function musterOf(input: MusterInput): Muster {
  const people = input.rows.filter(isPerson);
  const byId = new Map(input.roster.map((c) => [c.id, c]));
  const nets = networksOf(input.roster);
  const onStage = new Set(people.map((p) => p.combatantId));
  const memberships = new Map(people.map((p) => [p.combatantId, membershipOf(byId.get(p.combatantId), nets, onStage)]));
  const actorTag = input.actorId ? (membershipOf(byId.get(input.actorId), nets, onStage)?.tag ?? null) : null;
  const cx: ChipContext = { input, owed: new Set(input.owedIds), memberships, actorTag };
  const unitsOf = new Map<string, PersonRow[]>();
  for (const p of people) {
    const host = hostOf(p, cx);
    if (host) unitsOf.set(host, [...(unitsOf.get(host) ?? []), p]);
  }
  const hosts = people.filter((p) => !hostOf(p, cx));
  const blocks = hosts.map((p) => ({ row: p, block: blockOf(p, unitsOf.get(p.combatantId) ?? [], cx) }));
  return {
    inOrder: blocks.filter((b) => b.row.inOrder).map((b) => b.block),
    notInOrder: blocks.filter((b) => !b.row.inOrder).map((b) => b.block),
  };
}

/**
 * What folds into the "▴ N ACTED" stub when the column runs out of room: a block whose every chip has acted - a
 * group stays whole while one of its units still has to act - and never the block holding the actor.
 */
export function actedSplit(blocks: readonly MusterBlock[]): { acted: MusterBlock[]; rest: MusterBlock[] } {
  const folds = (b: MusterBlock) => b.chips.every((c) => c.acted && !c.acting);
  return { acted: blocks.filter(folds), rest: blocks.filter((b) => !folds(b)) };
}

/** Field by field: chips are rebuilt every render, so identity says nothing. */
export function sameChip(a: MusterChip, b: MusterChip): boolean {
  return (Object.keys(a) as (keyof MusterChip)[]).every((k) => a[k] === b[k]);
}

/** One chip's place in the structure: who, how deep, whether it has acted, whether it is the actor. */
const chipKey = (c: MusterChip) => `${c.id}:${c.depth}${c.acted ? 'a' : ''}${c.acting ? '*' : ''}`;

/** A section's blocks, one key. */
const sectionKey = (blocks: readonly MusterBlock[]) => blocks.map((b) => b.chips.map(chipKey).join(',')).join('|');

/** The column's structure - who, how deep, which section, what has acted - and nothing a wound changes. */
export function fitKey(m: Muster): string {
  return `${sectionKey(m.inOrder)}#${sectionKey(m.notInOrder)}`;
}
